import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    DateTimeintervalSettings,
    DateTimeintervalSettingsParts,
    TooltipSettings,
} from '@bazis/form/models/form-element.types';
import { debounceTime, startWith, tap } from 'rxjs/operators';
import { merge } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { BazisFormService } from '@bazis/form/services/form.service';
import moment, { Moment } from 'moment';
import { ConditionalValidators } from '@bazis/form/validators/conditionalValidators';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-date-single-timeinterval',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisInputDateSingleTimeintervalComponent
    extends BazisControlValueAccessor
    implements OnChanges, OnInit
{
    @Input() fieldSettings: DateTimeintervalSettings = null;

    @Input() fieldSettingsParts: DateTimeintervalSettingsParts = null;

    @Input() dateTooltipKey: string = null;

    @Input() dateTitleKey: string = null;

    @Input() dateNoteKey: string = null;

    @Input() timeTooltipKey: string = null;

    @Input() timeTitleKey: string = null;

    @Input() timeNoteKey: string = null;

    @Input() minDate: string = null;

    @Input() maxDate: string = null;

    // настройки отображения и работы тултипа
    @Input() tooltipDateSettings: TooltipSettings = null;

    @Input() tooltipTimeSettings: TooltipSettings = null;

    @Input() datePlaceholder: string = null;

    @Input() timePlaceholder: string = null;

    dateInputFormat: string;

    timeInputFormat: string;

    date = new FormControl('');

    timeInterval = new FormControl([]);

    ngOnInit() {
        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);

            this.date.addValidators(
                ConditionalValidators.required(
                    () => (this.ngControl.control as EntityFormControl).$config.required?._,
                ),
            );
            this.timeInterval.addValidators(
                ConditionalValidators.required(
                    () => (this.ngControl.control as EntityFormControl).$config.required?._,
                ),
            );
        }
        if (this.fieldSettings) this.fieldSettings.inputFormat ||= 'YYYY-MM-DD[T]HH:mm:ss.SSS';
        if (this.fieldSettingsParts) {
            this.fieldSettingsParts.dateFormat ||= 'YYYY-MM-DD';
            this.fieldSettingsParts.timeFormat ||= 'HH:mm';
        }

        this.dateInputFormat = 'YYYY-MM-DD';
        this.timeInputFormat = this.fieldSettings
            ? this.fieldSettings.inputFormat
            : `${this.fieldSettingsParts.dateFormat}[T]${this.fieldSettingsParts.timeFormat}`;

        merge(this.date.valueChanges, this.timeInterval.valueChanges)
            .pipe(
                tap(() => {
                    this.updateParentControl();
                }),
                untilDestroyed(this),
            )
            .subscribe();

        merge(this.date.statusChanges, this.timeInterval.statusChanges)
            .pipe(
                startWith(true),
                debounceTime(0),
                tap(() => {
                    if (this.date.status === 'VALID' && this.timeInterval.status === 'VALID') {
                        let errors = this.ngControl.control.errors;
                        if (!errors) return;
                        delete errors.componentError;
                        errors = Object.keys(errors).length > 0 ? errors : null;
                        this.ngControl.control.setErrors(errors);
                    } else {
                        this.ngControl.control.setErrors({
                            ...this.ngControl.control.errors,
                            componentError: { ...this.date.errors, ...this.timeInterval.errors },
                        });
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
        this.writeValue(this.ngControl.control.value);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.minDate || changes.maxDate) {
        }
    }

    updateParentControl() {
        if (!this.timeInterval.value) return;

        const dateMoment = moment(this.date.value, this.dateInputFormat);
        const date = dateMoment.isValid() ? dateMoment : null;
        const interval = this.timeInterval.value.map((v) => moment(v, this.timeInputFormat));
        let fromMoment: Moment;
        let toMoment: Moment;

        if (date && interval[0] && interval[0].isValid()) {
            fromMoment = moment(date)
                .hours(interval[0].hours())
                .minutes(interval[0].minutes())
                .seconds(interval[0].seconds())
                .milliseconds(interval[0].milliseconds());
        }
        if (date && interval[1] && interval[1].isValid()) {
            toMoment = moment(date)
                .hours(interval[1].hours())
                .minutes(interval[1].minutes())
                .seconds(interval[1].seconds())
                .milliseconds(interval[1].milliseconds());
        }
        if (this.fieldSettings) {
            let from = fromMoment ? fromMoment.format(this.fieldSettings.inputFormat) : null;
            let to = toMoment ? toMoment.format(this.fieldSettings.inputFormat) : null;

            this.onChange({
                [this.fieldSettings.fromFieldName]: from,
                [this.fieldSettings.toFieldName]: to,
            });
            return;
        }

        if (this.fieldSettingsParts) {
            let fromDate = fromMoment
                ? fromMoment.format(this.fieldSettingsParts.dateFormat)
                : null;
            let fromTime = fromMoment
                ? fromMoment.format(this.fieldSettingsParts.timeFormat)
                : null;
            let toTime = toMoment ? toMoment.format(this.fieldSettingsParts.timeFormat) : null;

            this.onChange({
                [this.fieldSettingsParts.fromDateFieldName]: fromDate,
                [this.fieldSettingsParts.fromTimeFieldName]: fromTime,
                [this.fieldSettingsParts.toTimeFieldName]: toTime,
            });
        }
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.date.disable();
            this.timeInterval.disable();
        } else {
            this.date.enable();
            this.timeInterval.enable();
        }
    }

    public writeValue(value): void {
        if (!value) {
            this.date.setValue('', { emitEvent: false });
            this.timeInterval.setValue(null, { emitEvent: false });
            return;
        }
        let from;
        let to;
        if (this.fieldSettings) {
            from = value[this.fieldSettings.fromFieldName];
            to = value[this.fieldSettings.toFieldName];
            this.date.setValue(from, { emitEvent: false });
            this.timeInterval.setValue(to ? [from, to] : from ? [from] : []);
        } else if (this.fieldSettingsParts) {
            const fromDate = value[this.fieldSettingsParts.fromDateFieldName];
            const fromTime = value[this.fieldSettingsParts.fromTimeFieldName]
                ? `${value[this.fieldSettingsParts.fromDateFieldName]}[T]${
                      value[this.fieldSettingsParts.fromTimeFieldName]
                  }`
                : null;
            const toTime = value[this.fieldSettingsParts.toTimeFieldName]
                ? `${value[this.fieldSettingsParts.fromDateFieldName]}[T]${
                      value[this.fieldSettingsParts.toTimeFieldName]
                  }`
                : null;
            this.date.setValue(fromDate, { emitEvent: false });
            this.timeInterval.setValue(fromTime ? [fromTime, toTime] : fromTime ? [fromTime] : []);
        }
    }
}
